import { useRef, useState } from 'react';
import { Button } from '../../Common';
import useHttpPostProjectFile from '../../../hooks/project/projectApi/useHttpPostProjectFile';
import { ProjectFileTypeEnum } from '@cyberlabrs/helpers';

type Props = {
    projectId: string;
    onUploadSuccess: () => void;
};

export default function ProjectFileUpload({ projectId, onUploadSuccess }: Props) {
    const inputRef = useRef<HTMLInputElement>(null);
    const { exec } = useHttpPostProjectFile();

    const [isDragging, setIsDragging] = useState(false);

    const getFileType = (file: File): ProjectFileTypeEnum => {
        if (file.type.startsWith('image/')) {
            return ProjectFileTypeEnum.IMG;
        }

        if (file.type === 'application/pdf') {
            return ProjectFileTypeEnum.PDF;
        }

        if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.type === 'application/vnd.ms-excel') {
            return ProjectFileTypeEnum.XLS;
        }

        const ext = file.name.split('.').pop()?.toLowerCase();

        if (ext === 'jpg' || ext === 'jpeg' || ext === 'png') return ProjectFileTypeEnum.IMG;
        if (ext === 'pdf') return ProjectFileTypeEnum.PDF;
        if (ext === 'xls' || ext === 'xlsx') return ProjectFileTypeEnum.XLS;

        throw new Error('Unsupported file type');
    };

    const handleFile = (file: File) => {
        exec({
            projectId,
            file,
            fileType: getFileType(file),
            name: file.name,
            onSuccess: () => onUploadSuccess(),
        });
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFile(file);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragging(false);

        const file = e.dataTransfer.files?.[0];
        if (file) handleFile(file);
    };

    return (
        <div className="project-file-upload__container">
            <h1 className="project-file-upload__title">Upload new files</h1>

            <div
                className={`project-file-upload ${isDragging ? 'is-dragging' : ''}`}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => {
                    e.preventDefault();
                    setIsDragging(true);
                }}
                onDragLeave={() => setIsDragging(false)}
                onDrop={onDrop}
            >
                <img className="project-file-upload__icon" alt="upload file" src="/svg/project-upload.svg" />
                <p>Select a file or drag and drop here</p>
                <p>JPG, XLSX or PDF, file size no more than 10MB</p>

                <input ref={inputRef} type="file" hidden onChange={onInputChange} />
            </div>

            <div className="project-file-upload__buttons">
                <Button text="Cancel" variant="text" color="blue" />
                <Button text="Upload File" onClick={() => inputRef.current?.click()} />
            </div>
        </div>
    );
}
